import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signinImageUrl, setSigninImageUrl] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/captcha`);
      setCaptchaId(data.captcha_id);
      setCaptchaQuestion(data.question);
      setCaptchaAnswer('');
    } catch { /* captcha load failed */ }
  }, []);

  useEffect(() => {
    loadCaptcha();
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/settings/header`);
        if (data && data.signin_image_filename) {
          setSigninImageUrl(`${BACKEND_URL}/api/settings/signin-image?t=${Date.now()}`);
        }
      } catch { /* optional */ }
      setSettingsLoaded(true);
    };
    fetchSettings();
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaAnswer.trim()) {
      setError('Please answer the security question');
      return;
    }
    setLoading(true);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        name, email, password,
        captcha_id: captchaId,
        captcha_answer: captchaAnswer
      });
      if (data.token) {
        localStorage.setItem('access_token', data.token);
      }
      // Use checkAuth to set the user in context
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      loadCaptcha();
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-4xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get Started
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Create your account to begin your study plan
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="John Doe"
                className="w-full"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full"
                data-testid="register-password-input"
              />
            </div>

            <div>
              <Label className="text-slate-700 font-medium mb-2 block">
                Security Check
              </Label>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-slate-800 bg-slate-100 px-3 py-1.5 rounded-md" data-testid="captcha-question">
                  {captchaQuestion || 'Loading...'}
                </span>
                <button
                  type="button"
                  onClick={loadCaptcha}
                  className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                  title="Get a new question"
                  data-testid="captcha-refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                required
                placeholder="Your answer"
                className="w-full"
                data-testid="captcha-answer-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-600 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className={`hidden lg:flex flex-1 relative overflow-hidden transition-colors duration-300 ${imageReady ? 'bg-slate-900' : 'bg-white'}`}>
        {settingsLoaded && signinImageUrl && (
          <img
            src={signinImageUrl}
            alt="Sign-in background"
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageReady ? 'opacity-80' : 'opacity-0'}`}
            onLoad={() => setImageReady(true)}
            data-testid="signin-background-image"
          />
        )}
        {imageReady && <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>}
      </div>
    </div>
  );
};